import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Calendar,
  Users,
  Clock,
  AlertCircle,
  TrendingUp,
  Loader2,
  ArrowLeftRight,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { shiftApi, employeeApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

// Type definitions
interface Shift {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  duration_hours?: number;
  is_active: boolean;
}

interface Schedule {
  id: string;
  name?: string;
  start_date: string;
  end_date: string;
  is_published: boolean;
}

interface Assignment {
  id: string;
  employee_id: string;
  shift_id: string;
  work_date: string;
  status: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name?: string;
}

const ShiftPlanning: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch shift data
  const { data: shifts = [], isLoading: shiftsLoading } = useQuery<Shift[]>({
    queryKey: ['shifts'],
    queryFn: async () => {
      const result = await shiftApi.shifts.getAll();
      return result as Shift[];
    },
  });

  const { data: schedules = [], isLoading: schedulesLoading } = useQuery<Schedule[]>({
    queryKey: ['schedules'],
    queryFn: async () => {
      const result = await shiftApi.schedules.getAll();
      return result as Schedule[];
    },
  });

  const { data: assignments = [] } = useQuery<Assignment[]>({
    queryKey: ['shiftAssignments'],
    queryFn: async () => {
      const result = await shiftApi.assignments.getAll({});
      return result as Assignment[];
    },
  });

  const { data: employees = [] } = useQuery<Employee[]>({
    queryKey: ['employees'],
    queryFn: async () => {
      const result = await employeeApi.getAll({});
      return result as Employee[];
    },
  });

  const isLoading = shiftsLoading || schedulesLoading;

  // Calculate metrics
  const activeShifts = shifts.filter((s) => s.is_active).length;
  const publishedSchedules = schedules.filter((s) => s.is_published).length;
  const totalAssignments = assignments.length;
  const confirmedAssignments = assignments.filter((a) => a.status === 'CONFIRMED').length;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return 'N/A';
    return timeStr.slice(0, 5);
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find((e) => e.id === employeeId);
    return employee
      ? `${employee.first_name} ${employee.last_name || ''}`.trim()
      : 'Unknown';
  };

  const getStatusBadge = (status: string) => {
    const isPositive = ['ACTIVE', 'PUBLISHED', 'CONFIRMED'].includes(status.toUpperCase());
    const classNames = isPositive 
      ? 'bg-emerald-50 text-emerald-705 border-emerald-250/50 dark:bg-emerald-950/20 dark:text-emerald-400' 
      : 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
    
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", classNames)} variant="outline">
        {status.toLowerCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 animate-pulse">
        <Loader2 className="h-8 w-8 animate-spin text-blue-650" />
        <span className="ml-2 mt-3 font-bold text-xs text-slate-400">Loading shift planning data...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Manage employee shifts, schedules, and coverage</h2>
          
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/shift-planning/swaps')}
            className="border-blue-200 text-[#143A62] hover:bg-blue-50 dark:border-blue-900/50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            <ArrowLeftRight className="h-4 w-4 shrink-0 mr-1.5" />
            <span>Shift Swaps</span>
          </Button>
          <Button
            onClick={() => navigate('/shift-planning/schedules')}
          >
            <Calendar className="h-4 w-4 shrink-0 mr-1.5" />
            <span>View Schedules</span>
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
            <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Active Shifts</CardTitle>
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 shrink-0">
              <Clock className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-5">
            <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{activeShifts}</div>
            <p className="text-xs font-bold text-slate-400 mt-1">
              {shifts.length} total shifts
            </p>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
            <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Schedules</CardTitle>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-500 shrink-0">
              <Calendar className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-5">
            <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{schedules.length}</div>
            <p className="text-xs font-bold text-slate-400 mt-1">
              {publishedSchedules} published
            </p>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
            <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Assignments</CardTitle>
            <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 shrink-0">
              <Users className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-5">
            <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{totalAssignments}</div>
            <p className="text-xs font-bold text-slate-400 mt-1">
              {confirmedAssignments} confirmed
            </p>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
            <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Coverage</CardTitle>
            <div className="w-8 h-8 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-500 shrink-0">
              <TrendingUp className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-5">
            <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
              {totalAssignments > 0 ? Math.round((confirmedAssignments / totalAssignments) * 100) : 0}%
            </div>
            <p className="text-xs font-bold text-slate-400 mt-1">Confirmation rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 mb-2 w-fit shadow-sm backdrop-blur-sm shrink-0 flex h-auto">
          <TabsTrigger value="overview" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Overview</TabsTrigger>
          <TabsTrigger value="shifts" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Shifts</TabsTrigger>
          <TabsTrigger value="schedules" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Schedules</TabsTrigger>
          <TabsTrigger value="assignments" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md shadow-none">Assignments</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
              <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
                <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Recent Schedules</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {schedules.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 font-semibold text-xs animate-pulse">
                    <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50 text-slate-300 dark:text-slate-700" />
                    <p>No schedules found</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {schedules.slice(0, 5).map((schedule) => (
                      <div key={schedule.id} className="flex justify-between items-center py-3 px-4 rounded-xl border border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all">
                        <div>
                          <p className="font-bold text-xs text-[#143A62] dark:text-blue-400">{schedule.name || 'Unnamed Schedule'}</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-1">
                            {formatDate(schedule.start_date)} - {formatDate(schedule.end_date)}
                          </p>
                        </div>
                        {getStatusBadge(schedule.is_published ? 'Published' : 'Draft')}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
              <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
                <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Types</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {shifts.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 font-semibold text-xs animate-pulse">
                    <Clock className="h-8 w-8 mx-auto mb-2 opacity-50 text-slate-300 dark:text-slate-700" />
                    <p>No shifts configured</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {shifts.slice(0, 5).map((shift) => (
                      <div key={shift.id} className="flex justify-between items-center py-3 px-4 rounded-xl border border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all">
                        <div>
                          <p className="font-bold text-xs text-[#143A62] dark:text-blue-400">{shift.name}</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-1">
                            {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                          </p>
                        </div>
                        {getStatusBadge(shift.is_active ? 'Active' : 'Inactive')}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Shifts Tab */}
        <TabsContent value="shifts" className="space-y-4">
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0 gap-4 flex-wrap">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Types</CardTitle>
              <Button 
                onClick={() => navigate('/shift-planning/templates')}
                variant="outline"
              >
                Manage Templates
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b-2 border-slate-55 dark:border-slate-800">
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Shift Name</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Time</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Duration</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shifts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs animate-pulse">
                        No shifts configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    shifts.map((shift) => (
                      <TableRow key={shift.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                        <TableCell className="px-8 py-4">
                          <div className="flex items-center space-x-2.5">
                            <div className="w-7 h-7 rounded-lg bg-[#143A62]/10 dark:bg-blue-650/20 text-[#143A62] dark:text-blue-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                              {shift.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                            </div>
                            <span className="font-bold text-xs text-[#143A62] dark:text-blue-400">{shift.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-semibold text-slate-700 dark:text-slate-300 py-4">
                          {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                        </TableCell>
                        <TableCell className="text-xs font-semibold text-slate-550 py-4">{shift.duration_hours || 'N/A'} hours</TableCell>
                        <TableCell className="px-8 py-4">
                          {getStatusBadge(shift.is_active ? 'Active' : 'Inactive')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0 gap-4 flex-wrap">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Schedules</CardTitle>
              <Button 
                onClick={() => navigate('/shift-planning/schedules')}
                variant="outline"
              >
                View All
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b-2 border-slate-55 dark:border-slate-800">
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Schedule</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Period</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs animate-pulse">
                        No schedules found
                      </TableCell>
                    </TableRow>
                  ) : (
                    schedules.map((schedule) => (
                      <TableRow key={schedule.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                        <TableCell className="px-8 py-4">
                          <div className="flex items-center space-x-2.5">
                            <div className="w-7 h-7 rounded-lg bg-[#143A62]/10 dark:bg-blue-650/20 text-[#143A62] dark:text-blue-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                              {(schedule.name || 'Unnamed').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                            </div>
                            <span className="font-bold text-xs text-[#143A62] dark:text-blue-400">{schedule.name || 'Unnamed'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-semibold text-slate-700 dark:text-slate-300 py-4">
                          {formatDate(schedule.start_date)} - {formatDate(schedule.end_date)}
                        </TableCell>
                        <TableCell className="px-8 py-4">
                          {getStatusBadge(schedule.is_published ? 'Published' : 'Draft')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-4">
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Assignments</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b-2 border-slate-55 dark:border-slate-800">
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Employee</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Date</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Shift</TableHead>
                    <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs animate-pulse">
                        No shift assignments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignments.slice(0, 10).map((assignment) => (
                      <TableRow key={assignment.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                        <TableCell className="px-8 py-4">
                          <div className="flex items-center space-x-2.5">
                            <div className="w-7 h-7 rounded-lg bg-[#143A62]/10 dark:bg-blue-650/20 text-[#143A62] dark:text-blue-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                              {getEmployeeName(assignment.employee_id).split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            <span className="font-bold text-xs text-[#143A62] dark:text-blue-400">{getEmployeeName(assignment.employee_id)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-semibold text-slate-700 dark:text-slate-300 py-4">
                          {formatDate(assignment.work_date)}
                        </TableCell>
                        <TableCell className="text-xs font-bold text-slate-655 py-4 capitalize">{assignment.shift_id.toLowerCase()}</TableCell>
                        <TableCell className="px-8 py-4">
                          {getStatusBadge(assignment.status || 'PENDING')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ShiftPlanning;